import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture, TAN_STANDING_SEAM_PINE_RIDGE } from "@/components/site/images";
import {
  HERNANDO_GALVALUME_FINISHED,
  CRYSTAL_RIVER_BLUE_SHINGLE,
  LECANTO_METAL_BEFORE,
  LECANTO_METAL_AFTER,
} from "@/components/site/project-images";
import { Phone, CalendarCheck } from "lucide-react";

const FAQS: { q: string; a: string }[] = [
  {
    q: "How quickly can Destination Roofing inspect my roof?",
    a: "Destination Roofing aims to contact new leads promptly, often within about one hour. Same-day or next-day inspections are commonly available depending on the schedule, weather, roof conditions, and property location.",
  },
  {
    q: "Who performs the roof inspection?",
    a: "Owner and licensed roofing contractor Deston Judy generally performs roof-replacement inspections personally. He gets onto the roof when weather, pitch, and safety conditions allow.",
  },
  {
    q: "Is the roof inspection free?",
    a: "Yes. Destination Roofing does not charge homeowners for a roof inspection or replacement estimate.",
  },
  {
    q: "How do I know whether I need repair or replacement?",
    a: "A relatively new roof with an isolated and clearly identifiable problem may be repairable. Replacement becomes more likely when deterioration is widespread, leaks repeatedly return, decking damage is extensive, or the roofing system is no longer dependable.",
  },
  {
    q: "What is included in the estimate?",
    a: "The standard proposal generally includes tear-off, underlayment, drip edge, standard flashing, ordinary ventilation, penetration treatment, permits, disposal, cleanup, and two sheets of replacement decking. Property-specific work and optional upgrades are listed separately.",
  },
  {
    q: "What happens if damaged plywood is found?",
    a: "The roof deck is inspected after tear-off. Water staining, softness, swelling, delamination, rot, or reduced structural strength may require replacement. Destination Roofing photographs damaged decking before removing it so the homeowner can see why the work is necessary.",
  },
  {
    q: "How much deposit is required?",
    a: "Destination Roofing generally requires a $500 good-faith deposit, with the remaining contract balance due after the job is completed. The signed agreement controls the final payment terms.",
  },
  {
    q: "How long is the estimate valid?",
    a: "Roof-replacement estimates are generally valid for 60 days. Once a binding agreement is signed within that period, Destination Roofing does not normally pass later material-price increases to the homeowner.",
  },
  {
    q: "Should I choose shingles or metal?",
    a: "Shingles are selected most often because they provide a lower initial cost and may better fit HOA requirements. Metal costs more initially but may appeal to homeowners who value longer expected service life and a different appearance. The best option depends on the home, budget, and long-term plans.",
  },
  {
    q: "What is the difference between exposed-fastener and standing-seam metal?",
    a: "Exposed-fastener roofing is significantly less expensive and is the most common metal option selected by Destination Roofing customers. Standing seam conceals the main fasteners and is considered a more premium system, but it carries a substantially higher initial cost.",
  },
  {
    q: "Does Destination Roofing offer financing?",
    a: "Destination Roofing does not currently offer a financing program. The company is evaluating future financing options.",
  },
  {
    q: "Does homeowners insurance pay for roof replacement?",
    a: "Coverage depends on the cause of damage, policy terms, exclusions, deductible, documentation, and the insurance company's decision. Destination Roofing can photograph visible conditions, prepare a roofing estimate, meet an adjuster when needed, and provide scope revisions. Policy interpretation and coverage decisions belong to the insurer, agent, attorney, or properly licensed insurance professional.",
  },
  {
    q: "What workmanship warranty is included?",
    a: "Standard full-roof installations generally include a 15-year Destination Roofing workmanship warranty. Qualifying Atlas Signature Select systems may receive a 20-year workmanship warranty. Exact coverage is controlled by the written warranty and contract.",
  },
  {
    q: "Will you compare another roofing estimate?",
    a: "Yes. Destination Roofing will review a written competing proposal and help the homeowner compare roof area, material, decking charges, warranties, included components, and contractor reputation. If the proposals are truly comparable, Destination Roofing may be able to match or improve the price.",
  },
];

const REVIEWS = [
  {
    name: "Matthew Pullen",
    text: "Super easy to work with! Competitive pricing. Easy to get a hold of, showed up promptly and the guys did a great job! Thanks Destin!",
  },
  {
    name: "Back Water Services",
    text: "We just had our roof replaced by Destination Roofing. They did an AMAZING job! Their communication throughout the project was excellent.",
  },
  {
    name: "Big Army",
    text: "I hired Destin to replace my roof and was nothing but pleased. During the process communication was timely and professional.",
  },
];

export const Route = createFileRoute("/roof-replacement")({
  head: () => ({
    meta: [
      { title: "Roof Replacement in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Owner-led roof replacement in Citrus County for shingle, metal, and low-slope roofs. Free inspection, competitive pricing, and strong workmanship warranties." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Roof Replacement in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Owner-led roof replacement in Citrus County for shingle, metal, and low-slope roofs. Free inspection, competitive pricing, and strong workmanship warranties." },
      { property: "og:url", content: canonical("/roof-replacement") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/roof-replacement") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Roof Replacement", href: "/roof-replacement" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Roof Replacement",
          serviceType: "Roof Replacement",
          description: "Owner-led roof replacement in Citrus County, FL — shingle, metal, and low-slope roofing systems installed to Florida wind standards.",
          url: canonical("/roof-replacement"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    }],
  }),
  component: Page,
});

function TrustRow() {
  const items = [
    "Free roof inspections",
    "Owner-performed inspections",
    "Same-day or next-day appointments often available",
    "Licensed and insured",
    "15-year workmanship warranty on standard full-roof installations",
    `License #${" "}${BUSINESS.licenseNumber}`,
    "$500 good-faith deposit",
    "Balance generally due at completion",
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ValueCards() {
  const cards = [
    { h: "Owner Involvement", p: "The licensed roofing contractor stays involved from inspection through final review." },
    { h: "Honest Recommendations", p: "Destination Roofing has talked homeowners out of unnecessary replacements when a proper repair could reasonably restore the roof." },
    { h: "Competitive Pricing", p: "Lean overhead allows the company to provide competitive pricing without cutting ordinary roof-system components from the proposal." },
    { h: "Warranty Accountability", p: "Destination Roofing's approach is to honor written workmanship warranties rather than search for excuses to avoid responsibility." },
  ];
  return (
    <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
      {cards.map((c) => (
        <div key={c.h} className="border border-border rounded-lg p-5 bg-card">
          <h3 className="font-display font-bold text-lg mb-2">{c.h}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{c.p}</p>
        </div>
      ))}
    </div>
  );
}

function RepairVsReplace() {
  return (
    <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
      <div className="border border-border rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Repair may be the better choice when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· The roofing system is relatively new</li>
          <li>· Damage is isolated</li>
          <li>· The leak source can be identified</li>
          <li>· Surrounding materials remain serviceable</li>
          <li>· The deck remains structurally sound</li>
          <li>· A proper repair can reasonably restore the affected area</li>
        </ul>
      </div>
      <div className="border-2 border-cta rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Replacement may be the better choice when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· Deterioration is widespread</li>
          <li>· Multiple roof slopes are affected</li>
          <li>· Leaks repeatedly return</li>
          <li>· Surface wear is extensive</li>
          <li>· Decking damage is widespread</li>
          <li>· Metal panels are rusting</li>
          <li>· Previous repairs continue failing</li>
          <li>· Repair costs would consume a substantial portion of a full replacement</li>
        </ul>
      </div>
    </div>
  );
}

function ProjectFigure({
  image, alt, heading, caption, to,
}: { image: React.ComponentProps<typeof ResponsivePicture>["image"]; alt: string; heading: string; caption: string; to: string }) {
  return (
    <figure className="border border-border rounded-xl overflow-hidden bg-card">
      <ResponsivePicture image={image} alt={alt} sizes="(min-width: 1024px) 33vw, 100vw" imgClassName="w-full h-auto" />
      <figcaption className="p-4">
        <h3 className="font-display font-bold text-base mb-1">{heading}</h3>
        <p className="text-sm text-muted-foreground mb-2">{caption}</p>
        <Link to={to} className="text-sm font-semibold text-brand hover:underline">Learn more →</Link>
      </figcaption>
    </figure>
  );
}

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Roof Replacement", href: "/roof-replacement" }]} />
      <PageHero showRating
        eyebrow="Roof Replacement"
        title="Roof Replacement in Citrus County, FL"
        subtitle="When repairs are no longer practical, Destination Roofing Inc. provides complete roof replacement for homeowners throughout Citrus County. Owner and licensed roofing contractor Deston Judy generally performs the inspection personally, explains what he finds, and helps the homeowner compare shingle, metal, and low-slope roofing options based on the actual roof."
      />
      <ServicePageBody title="Roof Replacement" service="Roof Replacement">
        <p>
          We believe homeowners should receive honest information before being asked to make a major decision. If a roof can reasonably be <Link to="/roof-repair">repaired</Link>, we will explain that option. If deterioration is widespread and replacement is the more reliable choice, we will explain why.
        </p>

        <TrustRow />

        <h2>A More Personal Roof-Replacement Experience</h2>
        <p>
          Large roofing companies often divide the process between a call center, salesperson, project manager, and crew leader. The person who sold the roof may never return to the property.
        </p>
        <p>
          Destination Roofing operates differently. Deston generally performs the inspection, explains the estimate, checks the work, and remains available throughout the project. The homeowner has direct access to the person whose name, license, and reputation are attached to the work.
        </p>
        <ValueCards />

        <h2>Signs Your Roof May Need Replacement</h2>
        <p>
          A leak does not automatically mean the entire roof must be replaced. Some leaks come from an isolated flashing, penetration, fastener, or damaged area that can be repaired. Replacement becomes more likely when the roofing system is deteriorating across multiple areas or can no longer provide dependable protection.
        </p>
        <h3>Shingle roof warning signs</h3>
        <ul>
          <li>Widespread granule loss</li>
          <li>Shingles that appear dark or black because asphalt is becoming exposed</li>
          <li>Shingles that appear shiny or glittering because fiberglass is exposed</li>
          <li>Widespread cracking, lifting, curling, or missing shingles</li>
          <li>Repeated leaks in different areas</li>
          <li>Multiple previous repairs</li>
          <li>Soft or deteriorated roof decking</li>
          <li>Widespread <Link to="/storm-damage-roof-repair">storm damage</Link></li>
          <li>Overall age combined with visible deterioration</li>
        </ul>
        <h3>Metal roof warning signs</h3>
        <ul>
          <li>Large numbers of screws backing out</li>
          <li>Widespread fastener-washer failure</li>
          <li>Failing sealant around penetrations and details</li>
          <li>Rusting or corroded panels</li>
          <li>Repeated leakage across multiple roof areas</li>
          <li>Metal that is too deteriorated to hold new fasteners reliably</li>
        </ul>
        <h3>Flat-roof warning signs</h3>
        <ul>
          <li>Widespread membrane deterioration</li>
          <li>Repeated seam or flashing failures</li>
          <li>Persistent leaks</li>
          <li>Water remaining on the roof for more than approximately 24 to 48 hours</li>
          <li>Drainage conditions that cannot be corrected with a limited repair</li>
          <li>Deteriorated insulation or roof deck</li>
        </ul>
        <p>
          Roof condition matters more than age alone. Installation quality, ventilation, tree coverage, sun exposure, maintenance, drainage, material, and storm history all affect roof performance.
        </p>

        <h2>Should Your Roof Be Repaired or Replaced?</h2>
        <RepairVsReplace />
        <p>
          A leaking metal roof does not always require complete replacement. When the panels remain structurally sound and corrosion is not widespread, Destination Roofing may recommend a complete re-screw instead. The existing screws are removed, wider-diameter long-life ZAC screws are installed so they properly bite into the existing openings, and penetrations and details are resealed. Learn more about our <Link to="/roof-inspection">roof inspection</Link> process or read about <Link to="/metal-roofing">metal roofing</Link>.
        </p>

        <h2>What Happens During a Free Roof Inspection?</h2>
        <ol>
          <li><strong>Talk with the homeowner.</strong> Deston first asks what the homeowner has noticed, where leaks appear, and whether the concern involves repair, replacement, storm damage, or an aging roof.</li>
          <li><strong>Roof access.</strong> He generally gets onto the roof unless weather, roof pitch, or safety conditions prevent safe access.</li>
          <li><strong>Problem-specific inspection.</strong> For repairs, he investigates the damaged area and may inspect interior ceilings or attic areas when needed to trace a leak. For replacements, he evaluates the roof surface and important details such as chimneys, electrical risers, oversized off-ridge vents, skylights, wall transitions, and other penetrations.</li>
          <li><strong>Photographs.</strong> Photographs are taken of roofs that are safely accessed and can be provided to the homeowner upon request.</li>
          <li><strong>Recommendation and estimate.</strong> Destination Roofing commonly prepares and explains the estimate while still at the property.</li>
        </ol>
        <p><strong>Roof inspections are free.</strong> Destination Roofing does not charge an inspection fee.</p>

        <h2>A Roof-Replacement Estimate Without Surprise Add-Ons</h2>
        <p>
          Our standard proposal is presented as a complete package rather than forcing the homeowner to calculate ordinary roofing components separately. It generally includes:
        </p>
        <ul>
          <li>Removal of the existing roofing material</li>
          <li>Standard underlayment</li>
          <li>Required starter and cap materials</li>
          <li>Drip edge</li>
          <li>Standard flashing work</li>
          <li>Pipe-boot and penetration treatment</li>
          <li>Standard ventilation</li>
          <li>Permit fees</li>
          <li>Required municipal or county inspection coordination</li>
          <li>Material delivery</li>
          <li>Disposal and dump fees</li>
          <li>Jobsite cleanup</li>
          <li>Magnetic nail cleanup</li>
          <li>Two sheets of replacement decking</li>
          <li>Final photographs</li>
          <li>Applicable written workmanship warranty</li>
        </ul>
        <p>
          Destination Roofing normally includes two sheets of replacement decking in a full-roof proposal. Additional damaged decking is documented with photographs before replacement and charged according to the signed agreement.
        </p>
        <p>
          Optional upgrades appear as separate line items so homeowners can decide whether they want them. Examples may include upgraded roofing systems, skylight replacement, chimney-cricket construction, <Link to="/third-nail-wind-mitigation">third-nail wind-mitigation work</Link>, additional wood or structural repair, specialty ventilation, and other property-specific requirements.
        </p>
        <p>
          Estimates are generally valid for 60 days. Once a binding contract is signed within the estimate-validity period, Destination Roofing does not normally pass later material-price increases to the homeowner.
        </p>

        <h2>How Destination Roofing Installs Shingle Roofs</h2>
        <p>
          Destination Roofing primarily installs Atlas shingle systems because Deston considers the shingles robust enough for harsh Florida heat and values the manufacturer's system-warranty options.
        </p>
        <h3>Atlas Pinnacle Pristine</h3>
        <p>Atlas Pinnacle Pristine is the company's most commonly installed architectural shingle.</p>
        <h3>Atlas Pinnacle Sun</h3>
        <p>Pinnacle Sun may be offered as an upgraded option when appropriate for the home, color selection, budget, and product availability.</p>
        <h3>System components</h3>
        <p>For qualifying Atlas system installations, Destination Roofing may use:</p>
        <ul>
          <li>Atlas Summit 60 synthetic underlayment</li>
          <li>Atlas Pro-Cut starter shingles</li>
          <li>Atlas Pinnacle field shingles</li>
          <li>Atlas Pro-Cut hip-and-ridge shingles</li>
        </ul>
        <h3>Fastening standard</h3>
        <ul>
          <li>1½-inch roofing nails</li>
          <li>Six coil-gun nails per field shingle</li>
          <li>Four nails per ridge-cap shingle as a Destination Roofing high-wind installation standard</li>
          <li>Hand fastening only where required for specific manufactured ventilation or detail components</li>
        </ul>
        <h3>Valleys and layout</h3>
        <ul>
          <li>Shingle courses are laid out using snapped lines to maintain straight installation.</li>
          <li>California-cut valleys are commonly used.</li>
          <li>Fasteners are kept away from vulnerable valley areas.</li>
          <li>Sidewall and penetration details receive appropriate flashing and roofing cement or sealant where required.</li>
        </ul>
        <h3>Flashing and penetrations</h3>
        <ul>
          <li>Existing flashing is exposed and evaluated during tear-off.</li>
          <li>Sound flashing may remain when it is still structurally serviceable.</li>
          <li>Severely rusted, perforated, or failed flashing is replaced.</li>
          <li>Lead pipe boots are commonly used.</li>
          <li>Electrical risers receive neoprene zipper boots.</li>
          <li>Skylight replacement is commonly recommended during reroofing because an aging skylight can become a future leak point.</li>
          <li>Chimneys wider than approximately 32 inches may require a cricket to divert water, depending on the roof and applicable requirements.</li>
        </ul>
        <h3>Ventilation</h3>
        <ul>
          <li>Aluminum ridge ventilation with appropriate fasteners</li>
          <li>Lower-profile Omni-style ridge vents</li>
          <li>Off-ridge ventilation when insufficient ridge length exists</li>
          <li>Evaluation of soffit intake and attic airflow</li>
        </ul>
        <p>
          Preview colors on your home with our <Link to="/atlas-shingle-colors">Atlas shingle-color visualizer</Link>, or learn more about <Link to="/shingle-roofing">shingle roofing</Link> and <Link to="/soffit">soffit</Link> work.
        </p>

        <h2>Metal Roofing Options for Citrus County Homes</h2>
        <p>Destination Roofing installs both exposed-fastener and concealed-fastener metal roofing systems.</p>
        <h3>Exposed-fastener systems</h3>
        <ul>
          <li>Tough Rib or Ultra Rib</li>
          <li>5V Crimp</li>
          <li>PBR panels where appropriate</li>
        </ul>
        <p>
          Exposed-fastener metal is significantly less expensive than standing seam and is the most common metal option selected by Destination Roofing customers. For residential exposed-fastener systems, Destination Roofing generally uses 26-gauge metal or heavier, depending on the approved system.
        </p>
        <h3>Standing-seam systems</h3>
        <ul>
          <li>Snap-lock standing seam with screw strip</li>
          <li>Snap-lock standing seam with clips</li>
          <li>Mechanically seamed standing seam</li>
        </ul>
        <p>Destination Roofing generally recommends approximately 24-gauge material for standing-seam residential systems, depending on engineering, manufacturer, and project requirements.</p>

        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
          <ResponsivePicture
            image={TAN_STANDING_SEAM_PINE_RIDGE}
            alt="Tan standing-seam metal roof installed on a new-construction home in Pine Ridge, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">New-construction <Link to="/projects/standing-seam-metal-roof-pine-ridge-fl">standing-seam metal roof in Pine Ridge, FL</Link>.</figcaption>
        </figure>

        <h3>Underlayment and details</h3>
        <ul>
          <li>High-temperature peel-and-stick underlayment is installed under metal roofing.</li>
          <li>Metal trim and flashing are integrated as new components of the roof system.</li>
          <li>Manufacturer-compatible, color-matched sealants are used.</li>
          <li>Long-life ZAC screws are used for applicable exposed-fastener systems.</li>
          <li>Fastener selection depends on whether the connection is wood-to-metal or metal-to-metal.</li>
        </ul>
        <p>
          Destination Roofing works with metal suppliers such as Tri County Metals, Premier Metals, and Metal Ops. See finished installations in our <Link to="/gallery">project gallery</Link> or read more about <Link to="/metal-roofing">metal roofing</Link>.
        </p>

        <h2>Flat and Low-Slope Roof Replacement</h2>
        <p>
          Lanais, additions, Florida rooms, and commercial roofs may require a system designed specifically for low slope. Destination Roofing works with multiple flat-roof systems, including TPO.
        </p>
        <ul>
          <li>GAF EverGuard TPO may be used on appropriate projects.</li>
          <li>Typical membrane options may include 45-mil and 60-mil TPO.</li>
          <li>Attachment may be mechanically fastened, adhered, or selected according to the approved roof design.</li>
          <li>Welded seams are tested.</li>
          <li>Drains, scuppers, gutters, and perimeter details are designed according to the building and plans.</li>
          <li>Tapered insulation may be used to create positive drainage where appropriate.</li>
          <li>Deston may inspect after rainfall to check drainage and ponding conditions.</li>
        </ul>
        <p>
          For eligible commercial TPO systems, Destination Roofing works with a separate GAF-certified installer who can qualify approved projects for a GAF NDL guarantee. Eligibility and term depend on the exact roof system, products, installer certification, inspection, and manufacturer approval. Learn more about <Link to="/flat-roofing">flat roofing</Link> and <Link to="/commercial-roofing">commercial roofing</Link>.
        </p>

        <h2>What Happens When Damaged Roof Decking Is Found?</h2>
        <p>
          The condition of the roof deck cannot be fully confirmed until the existing roofing material is removed. Water staining is an early warning that plywood or OSB may have lost strength or begun deteriorating. Destination Roofing also checks for softness, swelling, delamination, rot, loss of structural strength, failed plank or sheet connections, and damage around penetrations and leaks.
        </p>
        <p>Damaged decking is photographed before removal so the homeowner can see why the work is necessary.</p>
        <ul>
          <li>½-inch OSB is common and is the minimum sheet thickness generally used by Destination Roofing for replacement decking.</li>
          <li>Plywood may be installed when the homeowner prefers it, with cost adjusted accordingly.</li>
          <li>Older homes may have sturdy 1x8 plank decking.</li>
          <li>Two sheets are generally included in the roof-replacement package.</li>
          <li>Additional decking is handled according to the contract.</li>
        </ul>
        <h3>Structural and edge damage</h3>
        <p>If damaged fascia, soffit, bracing, rafters, or limited truss conditions are found:</p>
        <ul>
          <li>The homeowner is notified.</li>
          <li>Photographs are taken.</li>
          <li>Available repair options are explained.</li>
          <li>Destination Roofing can perform <Link to="/soffit">fascia and soffit replacement</Link>.</li>
          <li>Limited truss or rafter repairs may be performed when within the company's capabilities and permitted scope.</li>
          <li>Extensive structural damage may require additional engineering or another qualified professional.</li>
        </ul>

        <h2>Our Roof-Replacement Process</h2>
        <ol>
          <li><strong>Lead response.</strong> Our office secretary commonly receives the initial phone or website lead and enters the request into our project system. The company aims to respond promptly, often within about one hour.</li>
          <li><strong>Free inspection.</strong> Deston generally meets the homeowner and personally inspects the roof.</li>
          <li><strong>On-site estimate.</strong> The homeowner commonly receives and reviews the written estimate during the same appointment.</li>
          <li><strong>Material and option selection.</strong> The homeowner selects the roof system, material, color, and any optional upgrades.</li>
          <li><strong>Permits and scheduling.</strong> The office handles permits and schedules required county or city inspections.</li>
          <li><strong>Tear-off and installation.</strong> The existing roofing material is removed, the deck is inspected, hidden damage is documented, and the approved roofing system is installed.</li>
          <li><strong>Owner review and cleanup.</strong> Deston checks workmanship and cleanup. Final photographs are taken, and a final walkthrough or explanation is provided when the homeowner is available.</li>
        </ol>

        <h2>What Affects Roof-Replacement Cost in Citrus County?</h2>
        <p>
          Destination Roofing primarily prices reroofs according to the total roofing surface rather than the home's living square footage. Two homes with the same interior square footage can have very different roof sizes and complexity. Factors include:
        </p>
        <ul>
          <li>Total roof squares</li>
          <li>Roof pitch</li>
          <li>Number of valleys, hips, ridges, and roof sections</li>
          <li>One-story versus two-story access</li>
          <li>Existing roofing layers</li>
          <li>Shingle versus exposed-fastener metal</li>
          <li>Standing seam versus exposed-fastener metal</li>
          <li>Flat or low-slope roofing requirements</li>
          <li>Chimney size and cricket requirements</li>
          <li>Skylights</li>
          <li>Electrical risers</li>
          <li>Ventilation needs</li>
          <li>Hidden decking damage</li>
          <li>Fascia, soffit, or structural wood repairs</li>
          <li>Material gauge</li>
          <li>Drainage correction</li>
          <li>Tapered insulation</li>
          <li>Permit and disposal requirements</li>
          <li>Jobsite accessibility</li>
        </ul>
        <p>
          Our pricing is competitive because the company maintains lean overhead and direct owner involvement. Homeowners are not paying for a large sales staff, multiple management layers, or a large corporate office structure.
        </p>
        <p>
          If a homeowner receives another written proposal, Destination Roofing is willing to review it and compare the scope line by line. When the proposals are truly comparable, Deston may be able to match or improve the price. For a shingle-versus-metal breakdown, see our guide to <Link to="/blog/shingle-metal-flat-roofing-citrus-county">shingle, metal, and flat roofing in Citrus County</Link>.
        </p>
        <p>
          Learn how <Link to="/florida-roof-insurance-rules">Florida&apos;s roof insurance rules work</Link> and review the <Link to="/citrus-county-roof-permits">Citrus County re-roof permit process</Link> before your project begins.
        </p>
        <p>See our <Link to="/roof-cost-citrus-county">2026 roof-cost ranges for Citrus County</Link>.</p>

        <h2>Recent Roof-Replacement Projects</h2>
        <div className="not-prose grid gap-5 md:grid-cols-2 lg:grid-cols-3 my-6">
          <ProjectFigure
            image={HERNANDO_GALVALUME_FINISHED}
            alt="Completed Galvalume metal roof on a home in Hernando, Florida"
            heading="Galvalume Metal Roof in Hernando"
            caption="Completed Galvalume metal roofing system installed on a residential home in Hernando, Florida."
            to="/projects/galvalume-metal-roof-hernando-fl"
          />
          <ProjectFigure
            image={CRYSTAL_RIVER_BLUE_SHINGLE}
            alt="Completed blue architectural shingle roof in Crystal River, Florida"
            heading="Blue Shingle Roof in Crystal River"
            caption="Completed architectural shingle roofing project in Crystal River, Florida."
            to="/projects/blue-shingle-roof-crystal-river-fl"
          />
          <figure className="border border-border rounded-xl overflow-hidden bg-card">
            <div className="grid grid-cols-1 sm:grid-cols-2">
              <div>
                <ResponsivePicture
                  image={LECANTO_METAL_BEFORE}
                  alt="Deteriorated metal roof section before replacement in Lecanto, Florida"
                  sizes="(min-width: 640px) 16vw, 100vw"
                  imgClassName="w-full h-auto"
                />
                <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-muted text-muted-foreground">Before</div>
              </div>
              <div>
                <ResponsivePicture
                  image={LECANTO_METAL_AFTER}
                  alt="Completed replacement metal roof section in Lecanto, Florida"
                  sizes="(min-width: 640px) 16vw, 100vw"
                  imgClassName="w-full h-auto"
                />
                <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-cta text-black">After</div>
              </div>
            </div>
            <figcaption className="p-4">
              <h3 className="font-display font-bold text-base mb-1"><Link to="/roofing-company-lecanto-fl">Lecanto Metal Roof Before and After</Link></h3>
              <p className="text-sm text-muted-foreground mb-2">Existing deteriorated metal roofing before replacement and the completed replacement section afterward.</p>
              <Link to="/projects/lecanto-metal-roof-before-after" className="text-sm font-semibold text-brand hover:underline">View project details →</Link>
            </figcaption>
          </figure>
        </div>
        <p><Link to="/gallery">View More Roofing Projects</Link></p>

        <h2>Protecting the Home During Roof Replacement</h2>
        <ul>
          <li><strong>Landscaping.</strong> Tarps are placed to help protect landscaping and collect tear-off debris.</li>
          <li><strong>Driveways.</strong> Tarps or plywood may be used to protect newer or high-quality driveways when vehicles or equipment must access them.</li>
          <li><strong>Air-conditioning equipment.</strong> Plywood or another appropriate barrier may be positioned to help keep falling debris away from exterior air-conditioning units.</li>
          <li><strong>Pools.</strong> When roofing debris affects a pool area, the crew cleans the affected area after the work.</li>
        </ul>
        <p>
          <strong>Nail cleanup.</strong> Thousands of fasteners may be removed during a reroof. A single magnetic sweep is not enough. Destination Roofing repeatedly runs magnetic nail bars around the property because another nail may still be found even after many passes.
        </p>
        <p><strong>Final cleanup.</strong> Tarps are emptied and removed, roofing debris is collected, the yard and driveway receive repeated magnetic sweeps, the completed roof is photographed, and Deston personally checks overall workmanship and cleanup.</p>

        <h2>Written Workmanship Warranties We Intend to Honor</h2>
        <ul>
          <li><strong>Standard full-roof workmanship warranty.</strong> Destination Roofing generally provides a 15-year workmanship warranty on a standard full-roof installation.</li>
          <li><strong>Qualifying Atlas Signature Select roof.</strong> A qualifying Atlas Signature Select system may receive a 20-year Destination Roofing workmanship warranty.</li>
          <li><strong>Repair work.</strong> Repair work generally receives a five-year workmanship warranty on the areas Destination Roofing directly repairs.</li>
        </ul>
        <p>
          The workmanship warranty generally covers the labor and material required to correct leaks caused by Destination Roofing's improper installation, subject to the written contract, exclusions, and warranty terms.
        </p>
        <p>
          When qualifying Atlas shingles and system components are installed, the roof may qualify for Atlas Signature Select manufacturer warranty coverage. Exact coverage, registration requirements, exclusions, and term must follow the current official Atlas warranty documents.
        </p>
        <p>
          Destination Roofing believes warranties should mean something. If a legitimate installation-related problem occurs, the company's approach is to investigate the issue and take responsibility rather than automatically search for a reason to blame the homeowner.
        </p>

        <h2>Roof Replacement Built for Citrus County Weather</h2>
        <ul>
          <li>Strong Florida sun accelerates surface wear.</li>
          <li>Heat can expose weaknesses in low-quality materials.</li>
          <li>Heavy rain tests valleys, flashing, penetrations, and transitions.</li>
          <li>Humidity can worsen moisture-related deterioration.</li>
          <li>High winds can lift shingles and loosen seals without removing every shingle.</li>
          <li>Ridge caps with insufficient fastening can blow off.</li>
          <li>Metal fasteners and sealants require long-term maintenance.</li>
          <li>Low-slope areas require positive drainage.</li>
          <li>Roof-to-wall connections may be evaluated for <Link to="/third-nail-wind-mitigation">third-nail improvements</Link>.</li>
          <li><Link to="/soffit">Soffit intake</Link> and roof ventilation should function together.</li>
        </ul>
        <p>
          We serve homeowners across Citrus County — from <Link to="/roofing-company-beverly-hills-fl">Beverly Hills</Link>, <Link to="/roofing-company-inverness-fl">Inverness</Link>, and <Link to="/roofing-company-crystal-river-fl">Crystal River</Link> to <Link to="/roofing-company-homosassa-fl">Homosassa</Link>, <Link to="/roofing-company-lecanto-fl">Lecanto</Link>, and <Link to="/roofing-company-hernando-fl">Hernando</Link>. See all <Link to="/service-areas">service areas</Link> or our full list of <Link to="/services">services</Link>. Roof damaged in a storm? Read about <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link>.
        </p>

        <h2>What Homeowners Say About Destination Roofing</h2>
        <div className="not-prose grid gap-4 md:grid-cols-3 my-6">
          {REVIEWS.map((r) => (
            <blockquote key={r.name} className="border border-border rounded-lg p-5 bg-card">
              <p className="text-sm leading-relaxed">"{r.text}"</p>
              <footer className="mt-3 text-sm font-semibold">— {r.name}</footer>
            </blockquote>
          ))}
        </div>
        <p><Link to="/reviews">Read More Customer Reviews</Link></p>

        <h2>Roof-Replacement Guidance From an Experienced Local Roofer</h2>
        <p>
          This page is reviewed by Deston Judy, owner and licensed roofing contractor for Destination Roofing Inc. Deston began working in roofing at age 15 and now personally performs many of the company's inspections, reviews estimates with homeowners, checks project quality, and remains available throughout the work.
        </p>
        <ul>
          <li>Florida roofing license #{" "}{BUSINESS.licenseNumber}</li>
          <li>More than 100 roofs completed by Destination Roofing</li>
          <li>Atlas Scotchgard certification</li>
          <li>Approximately 15 years rooted in Citrus County</li>
        </ul>
        <p><Link to="/about">More about Deston and Destination Roofing →</Link></p>

        <h2>Roof-Replacement Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Get an Honest Roof-Replacement Recommendation</h2>
        <p>
          Whether the roof needs a limited repair or complete replacement, Destination Roofing will inspect the actual conditions and explain the reasonable options.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
